import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BullhornEntityTypes, BullhornNamespace } from 'datasource-service-lib';
import { SectionType } from 'enums-lib';
import { IDocumentDestinationService, IEntity, INamedEntity, TemplateWorkflowConfig } from 'interfaces-lib';
import { IDataSourcePathCommand } from 'interfaces-lib';
import { WorkflowMessage, WorkflowOptionsMessage } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { firstValueFrom, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { WorkflowTaskRunner } from 'workflow-task-service-lib';
import { runtimeEnvironment } from '../../../environments/environment';
import { CurrentUserOverrideStateService } from '../../services/current-user-override-state.service';
import { ServiceProviderService } from '../../services/service.provider.service';
import { TemplateWorkflowTask } from '../../tasks/template-workflow-task';
import { TemplateLaunchOverlayService } from '../../workflow-in-progress/template-launch-overlay.service';
import { AdActionsDialogComponent } from '../ad-actions-dialog/ad-actions-dialog.component';
import { AttachFileDialogComponent } from '../selection-dialogs/attach-file-dialog/attach-file-dialog.component';
import {
  IAttachFileDialogDataMultiEntity,
  IFileSectionConfig,
} from '../selection-dialogs/attach-file-dialog/datasource/datasource-attach-file.component';
import { DialogResult } from '../selection-dialogs/dialog-result';
import { EntitySelectDialogComponent } from '../selection-dialogs/entity-select-dialog/entity-select-dialog.component';
import { IGenerateDocumentWorkflowController } from '../template-workflow-types/igeneratedocumentworkflowcontroller';
import { EntitySelection, SectionFilesAttachmentPath, TemplateWorkflowContext } from '../template-workflow-types/templateworkflowcontext';
import { EntitySelectionPredicateFactory } from './entity-selection-predicate.factory';

// NOTE: the message sequence for the SIMPLE_DOCX_S3_SAVE workflow is:
//   GetModelFromDatasources, GettingWorkflowConfig, BuildingModel, GetModelFromDatasources, GeneratingDocument,
//   GetUserActionConfig, MergeDocxDocuments, UploadFile, SaveWorkflowState, SimpleDocxComplete,
//   options

@Injectable({ providedIn: 'root' })
export class WorkflowCoordinationService implements IGenerateDocumentWorkflowController {
  documentDestinationService: IDocumentDestinationService;

  constructor(
    private dialog: MatDialog,
    private messageNotificationService: MessageNotificationService,
    private serviceProviderService: ServiceProviderService,
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private identityStorage: IdentityStorage,
    private currentUserOverrideState: CurrentUserOverrideStateService,
    private templateLaunchOverlayService: TemplateLaunchOverlayService,
  ) {
    this.documentDestinationService = this.sharedServiceProviderServiceFactory
      .getServiceProviderService(this.identityStorage.get().namespace)
      .getDocumentDestinationService();
    console.debug('WorkflowCoordinationService created.');
  }

  start(primaryEntity: INamedEntity, template: TemplateWorkflowConfig): void {
    if (!this.assertWorkflow(template.workflow)) {
      this.templateLaunchOverlayService.hide();
      return;
    }

    (async () => {
      // primaryEntity is the first selected entity
      // user may select a second entity
      const entityDialogResult: DialogResult<EntitySelection> = await this.getSelectedEntity(primaryEntity, template);
      console.log('entityDialogResult', entityDialogResult);
      if (entityDialogResult.cancelled) {
        return;
      }
      const selectedSecondaryEntity: EntitySelection = entityDialogResult.data;

      // might need a third entity based on the selected entities (e.g. JobSubmission from Candidate + JobOrder)
      const tertiaryEntity: EntitySelection = await this.getAdditionalEntity(template, selectedSecondaryEntity, primaryEntity);

      // use those entities to get other entities which have attachments
      const attachmentEntities = await this.getAttachmentEntities(template, selectedSecondaryEntity?.source, tertiaryEntity?.source);
      const attachmentsDialogResult = await this.getAttachments(attachmentEntities, template);
      if (attachmentsDialogResult.cancelled) {
        return;
      }

      const override = this.currentUserOverrideState.current;
      const context: TemplateWorkflowContext = {
        entity: primaryEntity,
        templateWorkflowConfig: template,
        entitySelection: tertiaryEntity ?? selectedSecondaryEntity,
        sectionFilesAttachments: attachmentsDialogResult.data,
        ...(override ? { currentUserOverride: { id: override.id, name: override.name, email: override.email } } : {}),
      };
      this.startDocumentGenerationWorkflow(context);
    })();
  }

  documentGenerationSuccess(ctx: TemplateWorkflowContext, msg: WorkflowMessage): void {
    this.openActions(<WorkflowOptionsMessage>msg.message, ctx);
  }

  documentGenerationError(ctx: TemplateWorkflowContext, msg: WorkflowMessage): void {
    this.messageNotificationService.error(msg.message, null, msg.workflowJobId);
  }

  private getSelectedEntity(entity: INamedEntity, template: TemplateWorkflowConfig): Promise<DialogResult<EntitySelection>> {
    const showEntitySelection = EntitySelectionPredicateFactory.get(template.clientNamespace);
    if (!showEntitySelection(template, entity)) {
      return this.documentDestinationService
        .getDocumentDestinations(entity, template)
        .pipe(map((destinations) => new DialogResult(false, { source: entity, destinations })))
        .toPromise();
    }
    const dialogRef = this.dialog.open(EntitySelectDialogComponent, {
      ...EntitySelectDialogComponent.getDefaultMatConfig(),
      data: {
        namespace: template.clientNamespace,
        templateName: template.name,
        entity: entity,
        // Salesforce related templates can be launched from one entity type but require
        // selecting a record of the template entity type. Pass the template entity so
        // the selection dialog queries the correct target object instead of the launch entity.
        templateEntityType: template.entity,
      },
    });

    return dialogRef
      .afterClosed()
      .pipe(
        switchMap((dialogResult: DialogResult<INamedEntity[]>) => {
          console.log('entity-selection dialog closed', dialogResult);
          if (dialogResult.cancelled) {
            return of(DialogResult.CANCELLED);
          }

          return this.documentDestinationService.getDocumentDestinations(entity, template, dialogResult?.data).pipe(
            map((destinations) => {
              return new DialogResult(false, {
                source: { ...dialogResult.data[0] },
                destinations,
              });
            }),
          );
        }),
      )
      .toPromise();
  }

  private getAttachments(entities: INamedEntity[], template: TemplateWorkflowConfig): Promise<DialogResult<SectionFilesAttachmentPath[]>> {
    const attachmentsSection = template.sections.find((s) => s.type === SectionType.File && s.sourceType === 'USER');
    if (!attachmentsSection) {
      return of(new DialogResult(false, [])).toPromise();
    }

    const dialogData: IAttachFileDialogDataMultiEntity = {
      templateName: template.name,
      templateNamespace: template.clientNamespace,
      useFileAttachmentAsTemplate: template.useFileAttachmentAsTemplate,
      entities: entities,
      fileListProvider: this.serviceProviderService.getFileListProvider(template.clientNamespace),
      sectionConfig: attachmentsSection.config,
    };
    const dialogRef = this.dialog.open(AttachFileDialogComponent, {
      ...AttachFileDialogComponent.getDefaultMatConfig(),
      data: dialogData,
    });

    return dialogRef
      .afterClosed()
      .pipe(
        map((dialogResult: DialogResult<IDataSourcePathCommand[]>) => {
          console.log('attachment dialog closed', dialogResult);
          if (dialogResult.cancelled) {
            return DialogResult.CANCELLED;
          }

          return new DialogResult(false, [{ section: attachmentsSection, files: dialogResult.data }]);
        }),
      )
      .toPromise();
  }

  private startDocumentGenerationWorkflow(ctx: TemplateWorkflowContext): void {
    const workflowService = this.serviceProviderService.getWorkflowService(ctx.templateWorkflowConfig.clientNamespace);
    const workflowTask = new TemplateWorkflowTask(
      ctx.templateWorkflowConfig,
      ctx.entitySelection.source,
      ctx.entitySelection.destinations,
      ctx.sectionFilesAttachments,
      { context: ctx, controller: this },
      this.dialog,
    );
    const taskRunner = new WorkflowTaskRunner(workflowService, workflowTask);
    taskRunner.run().subscribe({
      error: (error) => {
        this.templateLaunchOverlayService.hide();
        this.messageNotificationService.error('The workflow could not be executed.', error);
      },
    });
  }

  private openActions(options: WorkflowOptionsMessage, context: TemplateWorkflowContext): void {
    this.dialog.open(AdActionsDialogComponent, {
      maxWidth: runtimeEnvironment.renderMode === 'web-app' ? '1600px' : '100vw',
      width: runtimeEnvironment.renderMode === 'web-app' ? '95vw' : '',
      panelClass: [
        runtimeEnvironment.renderMode === 'web-app' ? '' : 'chrome-ext-full-view',
        runtimeEnvironment.origin.namespace + '-namespace',
      ],
      height: 'auto',
      autoFocus: true,
      data: {
        options: options,
        templateWorkflowConfig: context.templateWorkflowConfig,
        entity: context.entity,
        entitySelection: context.entitySelection,
      },
    });
  }

  private assertWorkflow(workflow: string): boolean {
    if (workflow === 'SIMPLE_DOCX_S3_SAVE' || workflow === 'SIMPLE_DOCX') {
      return true;
    }

    this.messageNotificationService.error(`Unexpected workflow '${workflow}' starting document creation.`);
    console.error(`Unexpected workflow. Expecting either SIMPLE_DOCX_S3_SAVE or SIMPLE_DOCX, but got '${workflow}'.`);
    return false;
  }

  private async getAdditionalEntity(
    template: TemplateWorkflowConfig,
    entitySelection: EntitySelection,
    templateEntity: IEntity,
  ): Promise<EntitySelection> {
    // if entity is JobSubmission, use JobOrder and Candidate to find the JobSubmission ID
    // NOTE: one day we may need to hide this behind an interface

    if (template.clientNamespace !== BullhornNamespace.name) {
      return;
    }

    if (template.entity !== 'jobsubmission') {
      return;
    }

    const jobOrder = entitySelection.destinations.find((e) => e.type === 'JobOrder') ?? templateEntity;
    const candidate = entitySelection.destinations.find((e) => e.type === 'Candidate') ?? entitySelection.source;

    // TODO: remove cast when list2 becomes list
    const apiService = this.serviceProviderService.getDataSourceApiService(template.clientNamespace) as any;
    const jobSubmission = {
      entity: { type: BullhornEntityTypes.JobSubmission },
    };
    const query = `jobOrder.id:${jobOrder.id} AND candidate.id:${candidate.id} AND isDeleted:false`;
    const fields = 'id';

    return new Promise<EntitySelection>((resolve, reject) => {
      apiService.list2({ ...jobSubmission, query, fields }).subscribe((subs) => {
        if (subs.length === 0) {
          reject(new Error('No job submissions found.'));
          return;
        }

        console.log(
          `Replacing source (${entitySelection.source.type} #${entitySelection.source.id}) with (${BullhornEntityTypes.JobSubmission} #${subs[0].id})`,
        );
        const jobSubmissionEntity = {
          id: subs[0].id,
          name: '',
          type: BullhornEntityTypes.JobSubmission,
        };

        resolve({ ...entitySelection, source: jobSubmissionEntity });
      });
    });
  }

  private async getAttachmentEntities(template: TemplateWorkflowConfig, secondaryEntity: INamedEntity, tertiaryEntity: INamedEntity) {
    const fileSectionConfig: IFileSectionConfig = template.sections.find((s) => s.type === SectionType.File)?.config;
    const entityPathService = this.sharedServiceProviderServiceFactory
      .getServiceProviderService(template.clientNamespace)
      .getEntityPathService();
    const attachmentEntitiesObs = entityPathService.getEntitiesFromPaths(
      secondaryEntity,
      fileSectionConfig?.attachmentSourceEntities,
      tertiaryEntity,
    );
    return await firstValueFrom(attachmentEntitiesObs);
  }
}
